/**
 * Support Ticket Transformers
 * Pure field mapping functions
 */

import { getGatewayUrl } from '@ufc/core'
import type { SupportTicket, SupportTicketStats } from '../domain/types'
import type { ApiSupportTicketDto } from './api-types'

function resolveAttachmentUrl(url: string): string {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url
  }

  const baseUrl = getGatewayUrl()

  try {
    return new URL(url, baseUrl).toString()
  } catch {
    return `${baseUrl}${url}`
  }
}

export function transformSupportTicketToDomain(dto: ApiSupportTicketDto): SupportTicket {
  return {
    id: String(dto.id),
    documentId: dto.documentId ?? String(dto.id),
    ticketNumber: dto.ticketNumber,
    subject: dto.subject,
    description: dto.description,
    category: dto.category,
    channel: dto.channel,
    priority: dto.priority,
    status: dto.status,
    sourcePortal: dto.sourcePortal ?? 'admin_portal',
    referenceType: dto.referenceType ?? undefined,
    referenceId: dto.referenceId ?? undefined,
    contactEmail: dto.contactEmail ?? undefined,
    contactPhone: dto.contactPhone ?? undefined,
    notes: dto.notes ?? [],
    timeline: (dto.timeline ?? []).map((e) => ({
      type: e.type,
      at: e.at,
      actor: e.actor ?? undefined,
      from: e.from ?? undefined,
      to: e.to ?? undefined,
      text: e.text ?? undefined,
    })),
    attachments: (dto.attachments ?? []).map((a) => ({
      id: a.id,
      documentId: a.documentId ?? String(a.id),
      name: a.name,
      url: resolveAttachmentUrl(a.url),
      mime: a.mime ?? '',
      size: a.size ?? 0,
    })),
    assignedTo: dto.assignedTo ?? undefined,
    resolvedAt: dto.resolvedAt ?? undefined,
    slaStatus: dto.slaStatus ?? undefined,
    metadata: dto.metadata ?? undefined,
    userId: dto.user?.documentId ? String(dto.user.documentId) : undefined,
    tenantId: dto.tenant?.documentId ? String(dto.tenant.documentId) : undefined,
    tenantName: dto.tenant?.name ?? undefined,
    factoryId: dto.factory?.documentId ? String(dto.factory.documentId) : undefined,
    factoryName: dto.factory?.name ?? undefined,
    factoryCode: dto.factory?.code ?? undefined,
    createdAt: dto.createdAt ?? new Date().toISOString(),
    updatedAt: dto.updatedAt ?? new Date().toISOString(),
  }
}

export function transformSupportTicketsToDomain(dtos: ApiSupportTicketDto[]): SupportTicket[] {
  return dtos.map(transformSupportTicketToDomain)
}

export function computeTicketStats(tickets: SupportTicket[]): SupportTicketStats {
  return {
    open: tickets.filter((t) => t.status === 'open').length,
    inProgress: tickets.filter((t) => t.status === 'in_progress').length,
    resolved: tickets.filter((t) => t.status === 'resolved').length,
    total: tickets.length,
  }
}
